import { Injectable, OnDestroy } from '@angular/core';
import { DateAdapter, MatDateFormats } from '@angular/material/core';
import { TranslatorService } from '@sc/data-access-translation';
import { dayjs } from '@sc/utils';
import { Dayjs } from 'dayjs';
import { Subscription } from 'rxjs';

export const CUSTOM_DATE_FORMATS: MatDateFormats = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'DD/MM/YYYY',
    monthYearA11yLabel: 'MMM YYYY',
  },
};

@Injectable()
export class DayjsMaterialDateAdapterService
  extends DateAdapter<Dayjs>
  implements OnDestroy
{
  private readonly subs = new Subscription();

  constructor(private readonly translator: TranslatorService) {
    super();

    this.subs.add(
      this.translator.getCurrentLang().subscribe((newLang) => {
        this.setLocale(dayjs.locale());
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  getYear(date: Dayjs): number {
    return this.clone(date).year();
  }
  getMonth(date: Dayjs): number {
    return this.clone(date).month();
  }
  getDate(date: Dayjs): number {
    return this.clone(date).date();
  }
  getDayOfWeek(date: Dayjs): number {
    return this.clone(date).day();
  }
  getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    return style === 'long'
      ? dayjs.localeData().months()
      : dayjs.localeData().monthsShort();
  }
  getDateNames(): string[] {
    const valuesArray = Array(31);
    for (let i = 0; i < 31; i++) {
      valuesArray[i] = this.createDate(2017, 0, i + 1).format('D');
    }
    return valuesArray;
  }
  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (style === 'long') {
      return dayjs.localeData().weekdays();
    } else if (style === 'short') {
      return dayjs.localeData().weekdaysShort();
    } else {
      return dayjs.localeData().weekdaysMin();
    }
  }
  getYearName(date: Dayjs): string {
    return this.clone(date).format('YYYY');
  }
  getFirstDayOfWeek(): number {
    return 1;
  }
  getNumDaysInMonth(date: Dayjs): number {
    return this.clone(date).daysInMonth();
  }
  clone(date: Dayjs): Dayjs {
    return date.clone().locale(this.locale);
  }
  createDate(year: number, month: number, date: number): Dayjs {
    return dayjs().year(year).month(month).date(date).locale(this.locale);
  }
  today(): Dayjs {
    return dayjs().locale(this.locale);
  }
  parse(value: any, parseFormat: any): Dayjs {
    if (value && typeof value === 'string') {
      return dayjs(value, parseFormat, this.locale);
    }
    return value ? dayjs(value).locale(this.locale) : null;
  }
  format(date: Dayjs, displayFormat: any): string {
    return this.clone(date).format(displayFormat);
  }
  addCalendarYears(date: Dayjs, years: number): Dayjs {
    return this.clone(date).add(years, 'years');
  }
  addCalendarMonths(date: Dayjs, months: number): Dayjs {
    return this.clone(date).add(months, 'months');
  }
  addCalendarDays(date: Dayjs, days: number): Dayjs {
    return this.clone(date).add(days, 'days');
  }
  toIso8601(date: Dayjs): string {
    return this.clone(date).format();
  }
  /**
   * Returns the given value if given a valid Moment or null. Deserializes valid ISO 8601 strings
   * (https://www.ietf.org/rfc/rfc3339.txt) and valid Date objects into valid Moments and empty
   * string into null. Returns an invalid date for all other values.
   */
  override deserialize(value: any): Dayjs | null {
    let date;
    if (value instanceof Date) {
      date = dayjs(value).locale(this.locale);
    } else if (this.isDateInstance(value)) {
      // Note: assumes that cloning also sets the correct locale.
      return this.clone(value);
    }
    if (typeof value === 'string') {
      if (!value) {
        return null;
      }
      date = dayjs(value).locale(this.locale);
    }
    if (date && this.isValid(date)) {
      return dayjs(date).locale(this.locale);
    }
    return super.deserialize(value);
  }

  isDateInstance(obj: any): boolean {
    return dayjs.isDayjs(obj);
  }
  isValid(date: Dayjs): boolean {
    return this.clone(date).isValid();
  }
  invalid(): Dayjs {
    return dayjs('abcd');
  }
}
